import { useEffect, useRef, useState } from "react";
import { api, formatApiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Upload, ExternalLink, FileCheck2 } from "lucide-react";
import { toast } from "sonner";

export const VEHICLE_DOC_TYPES = [
  { key: "rc", label: "RC (Registration Certificate)" },
  { key: "insurance", label: "Insurance" },
  { key: "puc", label: "PUC Certificate" },
  { key: "permit", label: "Permit" },
  { key: "driving_license", label: "Driving License" },
];

export const VehicleDocsDialog = ({ vehicle, onClose, onChanged }) => {
  const [docs, setDocs] = useState({});
  const [uploading, setUploading] = useState("");
  const fileInputs = useRef({});
  const token = localStorage.getItem("ntc_token");
  useEffect(() => { setDocs(vehicle?.documents || {}); }, [vehicle]);

  const onFile = async (docKey, e) => {
    const file = e.target.files?.[0];
    if (!file || !vehicle?.id) return;
    setUploading(docKey);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(`/vehicles/${vehicle.id}/document/${docKey}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      setDocs((d) => ({ ...d, [docKey]: { path: res.data.path, filename: res.data.filename } }));
      onChanged && onChanged();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading("");
      if (fileInputs.current[docKey]) fileInputs.current[docKey].value = "";
    }
  };

  const docUrl = (doc) => `${API}/files/${doc.path}?auth=${token}`;

  return (
    <Dialog open={!!vehicle} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-[560px]" data-testid="vehicle-docs-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <FileCheck2 className="h-4 w-4 text-primary" /> Vehicle Documents — {vehicle?.registration_no}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-2 py-2">
          {VEHICLE_DOC_TYPES.map((d) => {
            const doc = docs[d.key];
            return (
              <div key={d.key} className="flex items-center justify-between gap-3 rounded-lg border border-border p-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium">{d.label}</div>
                  <div className="truncate text-xs text-muted-foreground">{doc ? doc.filename : "Not uploaded"}</div>
                </div>
                <input
                  ref={(el) => (fileInputs.current[d.key] = el)}
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp,.pdf"
                  className="hidden"
                  onChange={(e) => onFile(d.key, e)}
                  data-testid={`vehicle-file-input-${d.key}`}
                />
                <div className="flex shrink-0 gap-1">
                  {doc && (
                    <a href={docUrl(doc)} target="_blank" rel="noreferrer">
                      <Button variant="ghost" size="sm" className="h-8 gap-1 text-xs" data-testid={`vehicle-view-${d.key}`}>
                        <ExternalLink className="h-3.5 w-3.5" /> View
                      </Button>
                    </a>
                  )}
                  <Button
                    size="sm"
                    variant={doc ? "outline" : "default"}
                    className="h-8 gap-1 text-xs"
                    disabled={uploading === d.key}
                    onClick={() => fileInputs.current[d.key]?.click()}
                    data-testid={`vehicle-upload-${d.key}`}
                  >
                    <Upload className="h-3.5 w-3.5" />
                    {uploading === d.key ? "Uploading…" : doc ? "Replace" : "Upload"}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="vehicle-docs-close-btn">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
